import apiClient from './config';
import { 
  Category, 
  SubCategory, 
  QuestionCategory, 
  Question, 
  Game, 
  Topic,
  UserGameProgress,
  Achievement,
  UserAchievement,
  Leaderboard,
  ApiResponse,
  PaginationParams 
} from '../../types';

export const gamificationAPI = {
  // Categories
  getCategories: async (params?: PaginationParams): Promise<ApiResponse<Category[]>> => {
    const response = await apiClient.get('/gamification/categories', { params });
    return response.data;
  },

  createCategory: async (data: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Category>> => {
    const response = await apiClient.post('/gamification/categories', data);
    return response.data;
  },

  updateCategory: async (id: string, data: Partial<Category>): Promise<ApiResponse<Category>> => {
    const response = await apiClient.put(`/gamification/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/categories/${id}`);
    return response.data;
  },

  // Sub Categories
  getSubCategories: async (categoryId?: string): Promise<ApiResponse<SubCategory[]>> => {
    const params = categoryId ? { categoryId } : {};
    const response = await apiClient.get('/gamification/subcategories', { params });
    return response.data;
  },

  getSubCategoryById: async (id: string): Promise<ApiResponse<SubCategory>> => {
    const response = await apiClient.get(`/gamification/subcategories/${id}`);
    return response.data;
  },

  createSubCategory: async (data: Omit<SubCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<SubCategory>> => {
    const response = await apiClient.post('/gamification/subcategories', data);
    return response.data;
  },

  updateSubCategory: async (id: string, data: Partial<SubCategory>): Promise<ApiResponse<SubCategory>> => {
    const response = await apiClient.put(`/gamification/subcategories/${id}`, data);
    return response.data;
  },

  deleteSubCategory: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/subcategories/${id}`);
    return response.data;
  },

  // Question Categories
  getQuestionCategories: async (params?: PaginationParams): Promise<ApiResponse<QuestionCategory[]>> => {
    const response = await apiClient.get('/gamification/question-categories', { params });
    return response.data;
  },

  getQuestionCategoryById: async (id: string): Promise<ApiResponse<QuestionCategory>> => {
    const response = await apiClient.get(`/gamification/question-categories/${id}`);
    return response.data;
  },

  createQuestionCategory: async (data: Omit<QuestionCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<QuestionCategory>> => {
    const response = await apiClient.post('/gamification/question-categories', data);
    return response.data;
  },

  updateQuestionCategory: async (id: string, data: Partial<QuestionCategory>): Promise<ApiResponse<QuestionCategory>> => {
    const response = await apiClient.put(`/gamification/question-categories/${id}`, data);
    return response.data;
  },

  deleteQuestionCategory: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/question-categories/${id}`);
    return response.data;
  },

  // Questions
  getQuestions: async (params?: PaginationParams & { categoryId?: string }): Promise<ApiResponse<Question[]>> => {
    const response = await apiClient.get('/gamification/questions', { params });
    return response.data;
  },

  getQuestionById: async (id: string): Promise<ApiResponse<Question>> => {
    const response = await apiClient.get(`/gamification/questions/${id}`);
    return response.data;
  },

  createQuestion: async (data: Omit<Question, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Question>> => {
    const response = await apiClient.post('/gamification/questions', data);
    return response.data;
  },

  updateQuestion: async (id: string, data: Partial<Question>): Promise<ApiResponse<Question>> => {
    const response = await apiClient.put(`/gamification/questions/${id}`, data);
    return response.data;
  },

  deleteQuestion: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/questions/${id}`);
    return response.data;
  },

  // Games
  getGames: async (params?: PaginationParams & { subCategoryId?: string }): Promise<ApiResponse<Game[]>> => {
    const response = await apiClient.get('/gamification/games', { params });
    return response.data;
  },

  getGamesBySubCategory: async (subCategoryId: string): Promise<ApiResponse<Game[]>> => {
    const response = await apiClient.get(`/gamification/games/subcategory/${subCategoryId}`);
    return response.data;
  },

  createGame: async (data: Omit<Game, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Game>> => {
    const response = await apiClient.post('/gamification/games', data);
    return response.data;
  },

  updateGame: async (id: string, data: Partial<Game>): Promise<ApiResponse<Game>> => {
    const response = await apiClient.put(`/gamification/games/${id}`, data);
    return response.data;
  },

  deleteGame: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/games/${id}`);
    return response.data;
  },

  // Topics
  getTopics: async (questionCategoryId: string): Promise<ApiResponse<Topic[]>> => {
    const response = await apiClient.get(`/gamification/topics/category/${questionCategoryId}`);
    return response.data;
  },

  createTopic: async (data: Omit<Topic, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Topic>> => {
    const response = await apiClient.post('/gamification/topics', data);
    return response.data;
  },

  updateTopic: async (id: string, data: Partial<Topic>): Promise<ApiResponse<Topic>> => {
    const response = await apiClient.put(`/gamification/topics/${id}`, data);
    return response.data;
  },

  deleteTopic: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/gamification/topics/${id}`);
    return response.data;
  },

  // User Progress
  getUserProgress: async (userId?: string): Promise<ApiResponse<UserGameProgress[]>> => {
    const params = userId ? { userId } : {};
    const response = await apiClient.get('/gamification/user-progress', { params });
    return response.data;
  },

  submitGameResult: async (data: {
    gameId: string;
    score: number;
    timeSpent: number;
    answers: any[];
  }): Promise<ApiResponse<UserGameProgress>> => {
    const response = await apiClient.post('/gamification/user-progress', data);
    return response.data;
  },

  // Achievements
  getAchievements: async (): Promise<ApiResponse<Achievement[]>> => {
    const response = await apiClient.get('/gamification/achievements');
    return response.data;
  },

  getUserAchievements: async (userId?: string): Promise<ApiResponse<UserAchievement[]>> => {
    const params = userId ? { userId } : {};
    const response = await apiClient.get('/gamification/user-achievements', { params });
    return response.data;
  },

  getAchievementById: async (id: number): Promise<ApiResponse<Achievement>> => {
    const response = await apiClient.get(`/gamification/achievements/${id}`);
    return response.data;
  },

  getChallengeById: async (id: number): Promise<ApiResponse<any>> => {
    const response = await apiClient.get(`/gamification/challenges/${id}`);
    return response.data;
  },

  // Leaderboard
  getLeaderboard: async (params: {
    period: 'daily' | 'weekly' | 'monthly' | 'all-time';
    category?: string;
    limit?: number;
  }): Promise<ApiResponse<Leaderboard>> => {
    const response = await apiClient.get('/gamification/leaderboard', { params });
    return response.data;
  },

  // Daily Questions
  getDailyQuestions: async (): Promise<ApiResponse<Question[]>> => {
    const response = await apiClient.get('/gamification/daily-questions');
    return response.data;
  },

  createDailyQuestions: async (data: {
    questions: string[];
    date: string;
  }): Promise<ApiResponse<void>> => {
    const response = await apiClient.post('/gamification/daily-questions', data);
    return response.data;
  },

  // User Stats
  getUserStats: async (userId?: string): Promise<ApiResponse<{
    totalPoints: number;
    level: number;
    gamesCompleted: number;
    averageScore: number;
    streak: number;
    rank: number;
    achievements: number;
  }>> => {
    const params = userId ? { userId } : {};
    const response = await apiClient.get('/gamification/user-stats', { params });
    return response.data;
  },
};
